import { useCallback, useEffect, useRef, useState } from "react";
import type { ChatMessage, ChatMessagePage, ReplyPreview, SendMessageRequest, StaffMessageBody } from "@ferrivia/api-contract";

export interface ChatThreadTransport {
  listMessages: (after?: number) => Promise<ChatMessagePage>;
  send: (input: SendMessageRequest) => Promise<ChatMessage>;
}

export type OutboxEntryState = "pending" | "sent" | "failed";

export interface OutboxEntry {
  clientMessageId: string;
  body: StaffMessageBody;
  state: OutboxEntryState;
  createdAt: string;
  replyTo: ReplyPreview | null;
}

export interface UseChatThreadOptions {
  transport: ChatThreadTransport;
}

export interface UseChatThreadResult {
  messages: ChatMessage[];
  outbox: OutboxEntry[];
  send: (body: StaffMessageBody) => void;
  retry: (clientMessageId: string) => void;
  refresh: () => Promise<void>;
  replyTo: ChatMessage | null;
  setReplyTo: (message: ChatMessage | null) => void;
}

/**
 * The state machine. `transport` is a thin adapter so the client app can pass
 * `api.chat` and the clinic app a `patientId`-bound wrapper over
 * `api.clinicChat` with no branching in here.
 *
 * Reconciliation is by `clientMessageId`, never by text — but the read wire
 * schema (chatMessageSchema) doesn't carry `clientMessageId` back on polled
 * messages, so the send response's own `id`/`seq` (returned idempotently even
 * on a retried, already-persisted send) is what removes an outbox entry. A
 * later poll converges on the same state once the message enters `messages`.
 */
export function useChatThread({ transport }: UseChatThreadOptions): UseChatThreadResult {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [outbox, setOutbox] = useState<OutboxEntry[]>([]);
  const [replyTo, setReplyTo] = useState<ChatMessage | null>(null);
  const lastSeqRef = useRef(0);
  const transportRef = useRef(transport);
  transportRef.current = transport;
  const outboxRef = useRef<OutboxEntry[]>([]);
  useEffect(() => {
    outboxRef.current = outbox;
  }, [outbox]);

  const mergeMessages = useCallback((page: ChatMessagePage) => {
    if (page.messages.length === 0) {
      return;
    }
    setMessages((prev) => {
      const byId = new Map(prev.map((m) => [m.id, m]));
      for (const msg of page.messages) {
        byId.set(msg.id, msg);
      }
      return Array.from(byId.values()).sort((a, b) => a.seq - b.seq);
    });
    lastSeqRef.current = Math.max(lastSeqRef.current, page.lastSeq);
  }, []);

  const refresh = useCallback(async () => {
    const page = await transportRef.current.listMessages(lastSeqRef.current || undefined);
    mergeMessages(page);
  }, [mergeMessages]);

  const attemptSend = useCallback(
    (clientMessageId: string, body: StaffMessageBody, replyToId?: string) => {
      transportRef.current
        .send({ clientMessageId, body, replyToId })
        .then((message) => {
          setMessages((prev) => {
            if (prev.some((m) => m.id === message.id)) {
              return prev;
            }
            return [...prev, message].sort((a, b) => a.seq - b.seq);
          });
          lastSeqRef.current = Math.max(lastSeqRef.current, message.seq);
          setOutbox((prev) => prev.filter((entry) => entry.clientMessageId !== clientMessageId));
        })
        .catch(() => {
          setOutbox((prev) =>
            prev.map((entry) => (entry.clientMessageId === clientMessageId ? { ...entry, state: "failed" } : entry)),
          );
        });
    },
    [],
  );

  const send = useCallback(
    (body: StaffMessageBody) => {
      const clientMessageId = crypto.randomUUID();
      const currentReply = replyTo;
      const replyPreview: ReplyPreview | null = currentReply
        ? {
            id: currentReply.id,
            authorType: currentReply.authorType,
            authorName: currentReply.authorName,
            body: currentReply.body,
            redactedAt: currentReply.redactedAt,
          }
        : null;
      setOutbox((prev) => [
        ...prev,
        { clientMessageId, body, state: "pending", createdAt: new Date().toISOString(), replyTo: replyPreview },
      ]);
      attemptSend(clientMessageId, body, currentReply?.id);
      setReplyTo(null);
    },
    [attemptSend, replyTo],
  );

  const retry = useCallback(
    (clientMessageId: string) => {
      const entry = outboxRef.current.find((e) => e.clientMessageId === clientMessageId);
      if (!entry) {
        return;
      }
      setOutbox((prev) => prev.map((e) => (e.clientMessageId === clientMessageId ? { ...e, state: "pending" } : e)));
      attemptSend(clientMessageId, entry.body, entry.replyTo?.id);
    },
    [attemptSend],
  );

  return { messages, outbox, send, retry, refresh, replyTo, setReplyTo };
}
